// Bootstrap's CSS only. Its JavaScript (and jQuery) is not loaded: nothing in
// the app uses a data-bs-* attribute or the bootstrap.* API — modals, menus and
// tooltips are MUI — so it was ~80KB parsed on every page for nothing.
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import { beginBrowserSession } from './_helpers/auth/secureToken';
import { initTracking } from './_services/tracking/tracking';

// Everything else — React, the app, the store, Firebase — is its own chunk
// group, which public/index.html fetches alongside this file. main.js itself is
// only this file, so a page drawn from the last visit's copy (public/index.html)
// is on screen before any of the app's code runs: the app starts once that copy
// has painted (window.__gnPreviewPainted, capped there), or straight away when
// there is no copy. No JSX here for the same reason — it would pull React into
// main.js.
const loadApp = () => Promise.all([
  import('react'),
  import('react-dom/client'),
  import('react-redux'),
  import('./App'),
  import('./redux/store'),
]);

const renderApp = () => loadApp().then(([react, reactDomClient, { Provider }, { default: App }, { default: store }]) => {
  const React = react.default || react;
  const { createRoot } = reactDomClient.default || reactDomClient;
  // React 18's root: state updates are batched wherever they happen — after an
  // await, in a timer, in a Firebase callback — so a screen that sets five pieces
  // of state when its data lands renders once, not five times.
  createRoot(document.getElementById('root')).render(
    React.createElement(
      React.StrictMode,
      null,
      React.createElement(Provider, { store }, React.createElement(App)),
    ),
  );
  import('./reportWebVitals').then(({ default: reportWebVitals }) => reportWebVitals());
});

const signOutFirebase = () => Promise.all([import('firebase/auth'), import('./firebase')])
  .then(([{ signOut }, { auth }]) => signOut(auth));

// The app's own session dies with the browser (session cookies, see
// secureToken.js), but Firebase keeps its sign-in in IndexedDB, which does not.
// Left alone, a public page that turns a live Firebase user into an app token
// (/auth/email-verified) would sign the next person at this computer in as the
// last one. So the first page of a new browser session signs Firebase out too,
// before anything renders. Capped, so a hung call can never leave the app blank.
const newBrowserSession = beginBrowserSession();

// Marketing tracking (DEV_02): campaign params on this URL go into the shared
// gnc_attr cookie (only with analytics or advertising consent; remembered until
// consent is given, deleted if it is refused), and one delegated listener
// reports WhatsApp link clicks. Never throws.
initTracking();

(window.__gnPreviewPainted || Promise.resolve()).then(() => {
  if (newBrowserSession) {
    Promise.race([
      signOutFirebase(),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]).catch(() => {}).then(renderApp);
  } else {
    renderApp();
  }
});
